import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as THREE from 'three';
import { useTranslation } from 'react-i18next';
import { Dropdown } from 'antd';
import { GlobalOutlined } from '@ant-design/icons';
import { useAuthStore } from '../stores/auth';
import { createScene, setupLights, createBackgroundGrid } from '../three/scene';
import { createCamera } from '../three/camera';
import { createRenderer } from '../three/renderer';
import { createBlueprintLines, setupScatteredPositions } from '../three/blueprint';
import { create3DWalls, create3DFloor, create3DFurniture, BuildingConfig } from '../three/building';
import { IndoorStoryAnimation, ScrollController } from '../three/animation';
import { captureGrowthAttribution, trackGrowthEvent, withGrowthQuery } from '../utils/growthTracking';
import FeedbackButton from '../components/feedback/FeedbackButton';
import logoImg from '@/assets/kimap-logo-dark.png';
import './IndoorStory.css';

/**
 * 沉浸式 3D 室内定位展示页
 * 苹果官网风格的动画体验
 */

type StorySectionId =
  | 'hero'
  | 'blueprintUpload'
  | 'wallGeneration'
  | 'furnitureFocus'
  | 'sdkPositioning'
  | 'pricing';

type SectionPhase = 'enter' | 'hold' | 'exit';

interface StoryTimelineSection {
  id: StorySectionId;
  start: number;
  end: number;
  // within section localProgress(0..1)
  enterEnd: number;
  holdEnd: number;
}

interface SectionRuntime {
  id: StorySectionId;
  localProgress: number;
  phase: SectionPhase;
  phaseProgress: number;
  alpha: number; // 0..1 for content visibility
}

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

const getPhaseRuntime = (localProgress: number, enterEnd: number, holdEnd: number): Omit<SectionRuntime, 'id'> => {
  const lp = clamp01(localProgress);
  const eEnd = clamp01(enterEnd);
  const hEnd = clamp01(Math.max(holdEnd, eEnd));

  if (lp < eEnd) {
    const t = eEnd <= 0 ? 1 : lp / eEnd;
    return { localProgress: lp, phase: 'enter', phaseProgress: clamp01(t), alpha: clamp01(t) };
  }

  if (lp < hEnd) {
    return { localProgress: lp, phase: 'hold', phaseProgress: 0, alpha: 1 };
  }

  const denom = 1 - hEnd;
  const t = denom <= 0 ? 1 : (lp - hEnd) / denom;
  return { localProgress: lp, phase: 'exit', phaseProgress: clamp01(t), alpha: clamp01(1 - t) };
};

// 电影化滚轮叙事时间轴（0..1）
// 重点：每段 hold 占比极大，确保“出现→完整展示→停留→滚动很多次→才开始退出”
const storyTimeline: StoryTimelineSection[] = [
  { id: 'hero', start: 0.0, end: 0.05, enterEnd: 0.0, holdEnd: 0.78 },
  { id: 'blueprintUpload', start: 0.05, end: 0.18, enterEnd: 0.12, holdEnd: 0.82 },
  { id: 'wallGeneration', start: 0.18, end: 0.60, enterEnd: 0.12, holdEnd: 0.80 },
  { id: 'furnitureFocus', start: 0.60, end: 0.82, enterEnd: 0.12, holdEnd: 0.84 },
  // SDK：轨迹完成后需要明显停留（加长段落 + hold 更长）
  { id: 'sdkPositioning', start: 0.82, end: 0.96, enterEnd: 0.10, holdEnd: 0.92 },
  { id: 'pricing', start: 0.96, end: 1.0, enterEnd: 0.2, holdEnd: 0.95 },
];

interface StoryCopy {
  title: string;
  subtitle: string;
  detail?: string;
  button?: { text: string; url: string } | null;
}

const buildStoryCopy = (tFn: any): Record<StorySectionId, StoryCopy> => ({
  hero: {
    title: tFn('indoorStory.hero.title', '从平面地图，到空间智能'),
    subtitle: tFn('indoorStory.hero.subtitle', '几分钟内，将 2D 图纸构建为可交互的 3D 数字空间。')
  },
  blueprintUpload: {
    title: tFn('indoorStory.blueprintUpload.title', '一键上传平面地图'),
    subtitle: tFn('indoorStory.blueprintUpload.subtitle', '支持多格式平面地图结构化地图。'),
    detail: tFn('indoorStory.blueprintUpload.detail', 'Blueprint Parsing · JPG Import · Architectural Reconstruction')
  },
  wallGeneration: {
    title: tFn('indoorStory.wallGeneration.title', '让 2D 地图快速生长为 3D 空间'),
    subtitle: tFn('indoorStory.wallGeneration.subtitle', '无需复杂建模流程，即可快速生成可交互室内场景。'),
    detail: tFn('indoorStory.wallGeneration.detail', '结构先行：墙体与空间边界优先构建。')
  },
  furnitureFocus: {
    title: tFn('indoorStory.furnitureFocus.title', '丰富的 3D 建模资产库'),
    subtitle: tFn('indoorStory.furnitureFocus.subtitle', '通过大量可复用室内组件，快速构建真实空间环境。'),
    detail: tFn('indoorStory.furnitureFocus.detail', '沙发 · 桌椅 · 床 · 建筑细节 · 专业级资产生态')
  },
  sdkPositioning: {
    title: tFn('indoorStory.sdkPositioning.title', '开放 SDK 与空间 API'),
    subtitle: tFn('indoorStory.sdkPositioning.subtitle', '将室内定位、导航、轨迹追踪与空间分析能力集成到你的应用中。'),
    detail: tFn('indoorStory.sdkPositioning.detail', '企业级 · 精准 · 空间计算'),
    button: { text: tFn('indoorStory.sdkPositioning.button', 'Get SDK'), url: '/sdk-doc' }
  },
  pricing: {
    title: tFn('indoorStory.pricing.title', 'Flexible Pricing'),
    subtitle: tFn('indoorStory.pricing.subtitle', 'Choose the plan that fits your needs'),
    detail: tFn('indoorStory.pricing.detail', 'Starting from ¥0 · No credit card required')
  }
});

const buildPricingPlans = (tFn: any) => [
  { name: tFn('indoorStory.plans.free.name', 'Free'), price: tFn('indoorStory.plans.free.price', 'Free'), features: tFn('indoorStory.plans.free.features', '1 map · 100MB · Basic') },
  // { name: tFn('indoorStory.plans.personal.name', 'Personal'), price: tFn('indoorStory.plans.personal.price', '¥299'), features: tFn('indoorStory.plans.personal.features', '3 maps · 500MB · Priority') },
  // { name: tFn('indoorStory.plans.professional.name', 'Professional'), price: tFn('indoorStory.plans.professional.price', '¥799'), features: tFn('indoorStory.plans.professional.features', '10 maps · 2GB · API · Advanced') },
  // { name: tFn('indoorStory.plans.enterprise.name', 'Enterprise'), price: tFn('indoorStory.plans.enterprise.price', '¥1999'), features: tFn('indoorStory.plans.enterprise.features', 'Unlimited · 10GB · Dedicated · Custom') },
];

const IndoorStory: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { token } = useAuthStore();

  useEffect(() => {
    captureGrowthAttribution();
    trackGrowthEvent('landing_view', '/');
  }, []);

  const storyCopy = buildStoryCopy(t);
  const pricingPlans = buildPricingPlans(t);

  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const animationRef = useRef<IndoorStoryAnimation | null>(null);
  const scrollControllerRef = useRef<ScrollController | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  const [progress, setProgress] = useState(0);
  const [showHint, setShowHint] = useState(true);

  // 初始化 Three.js 场景
  const initScene = useCallback(() => {
    if (!containerRef.current || !canvasRef.current) return;

    const container = containerRef.current;
    const canvas = canvasRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight;

    // 创建场景
    const scene = createScene();
    sceneRef.current = scene;

    // 添加背景网格
    createBackgroundGrid(scene);

    // 设置光照
    setupLights(scene);

    // 创建相机
    const camera = createCamera(width / height);
    cameraRef.current = camera;

    // 创建渲染器
    const renderer = createRenderer(canvas, width, height);
    rendererRef.current = renderer;

    // 创建蓝图线稿
    const blueprint = createBlueprintLines();

    // 创建蓝图组
    const blueprintGroup = new THREE.Group();
    blueprintGroup.name = 'blueprint';

    // 添加各层线稿
    blueprintGroup.add(blueprint.wallLines);
    blueprintGroup.add(blueprint.doorLines);
    blueprintGroup.add(blueprint.windowLines);
    blueprintGroup.add(blueprint.furnitureLines);

    // 设置散落状态
    setupScatteredPositions(blueprintGroup);

    scene.add(blueprintGroup);

    // 创建 3D 建筑
    const buildingConfig: BuildingConfig = {
      wallHeight: 1.5,
      wallThickness: 0.15,
      floorThickness: 0.1,
      ceilingHeight: 1.4
    };

    const buildingGroup = new THREE.Group();
    buildingGroup.name = 'building';

    const walls = create3DWalls(buildingConfig);
    const floor = create3DFloor(buildingConfig);
    const furniture = create3DFurniture();

    buildingGroup.add(walls);
    buildingGroup.add(floor);
    buildingGroup.add(furniture);

    // 3D建筑初始隐藏
    buildingGroup.visible = false;

    scene.add(buildingGroup);

    // 创建动画控制器
    animationRef.current = new IndoorStoryAnimation(
      scene,
      camera,
      blueprintGroup,
      buildingGroup
    );

    // 创建滚动控制器
    scrollControllerRef.current = new ScrollController(
      container,
      (newProgress) => {
        setProgress(newProgress);
        animationRef.current?.updateProgress(newProgress);

        if (newProgress > 0.02) {
          setShowHint(false);
        }
      }
    );

    // 渲染循环
    const animate = () => {
      animationFrameRef.current = requestAnimationFrame(animate);

      if (rendererRef.current && sceneRef.current && cameraRef.current) {
        rendererRef.current.render(sceneRef.current, cameraRef.current);
      }
    };
    animate();

    // 处理窗口大小变化
    const handleResize = () => {
      if (!containerRef.current || !rendererRef.current || !cameraRef.current) return;

      const newWidth = containerRef.current.clientWidth;
      const newHeight = containerRef.current.clientHeight;

      cameraRef.current.aspect = newWidth / newHeight;
      cameraRef.current.updateProjectionMatrix();
      rendererRef.current.setSize(newWidth, newHeight);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      scrollControllerRef.current?.dispose();
      animationRef.current?.dispose();
      rendererRef.current?.dispose();
    };
  }, []);

  // 初始化场景
  useEffect(() => {
    const cleanup = initScene();
    return () => {
      cleanup?.();
    };
  }, [initScene]);

  const getCurrentSectionRuntime = useCallback((): SectionRuntime => {
    const p = clamp01(progress);

    const active = storyTimeline.find((s) => p >= s.start && p < s.end) || storyTimeline[storyTimeline.length - 1];
    const denom = active.end - active.start;
    const localProgress = denom <= 0 ? 1 : (p - active.start) / denom;
    const runtime = getPhaseRuntime(localProgress, active.enterEnd, active.holdEnd);

    return {
      id: active.id,
      ...runtime,
    };
  }, [progress]);

  const sectionRuntime = getCurrentSectionRuntime();

  // 定价是否显示：到达 pricing 段后保持显示（避免 100% 附近的滚轮抖动导致 alpha 进入 exit 而闪烁/消失）
  const showPricing = sectionRuntime.id === 'pricing';

  // pricing 段落使用独立的 pricing 面板，避免与通用文案层叠加造成重复
  const currentText = sectionRuntime.id === 'pricing' ? null : storyCopy[sectionRuntime.id];

  const isHero = sectionRuntime.id === 'hero';
  const isHeroTransition = false;

  return (
    <div
      ref={containerRef}
      className={`indoor-story-container ${isHero ? 'is-hero' : ''}`}
      data-section={sectionRuntime.id}
    >
      {/* Three.js Canvas */}
      <canvas ref={canvasRef} className="indoor-story-canvas" />

      {/* 顶部导航 */}
      <header className="indoor-story-header">
        <div className="indoor-story-logo">
          <img src={logoImg} alt="KiMap" className="logo-img h-6" />
        </div>
        <nav className="indoor-story-nav">
          <div className="nav-lang">
            <Dropdown
              placement="bottomRight"
              trigger={['click']}
              menu={{
                items: [
                  { key: 'en-US', label: 'English' },
                  { key: 'es-ES', label: 'Español' },
                  { key: 'fr-FR', label: 'Français' },
                ],
                onClick: ({ key }) => {
                  const lng = String(key);
                  i18n.changeLanguage(lng);
                  window.localStorage.setItem('i18nextLng', lng);
                },
              }}
            >
              <button
                type="button"
                className="lang-trigger"
                aria-label="Language"
              >
                <GlobalOutlined className="lang-icon" />
                <span className="lang-label">
                  {i18n.language.startsWith('es') ? 'Español' : i18n.language.startsWith('fr') ? 'Français' : 'English'}
                </span>
              </button>
            </Dropdown>
          </div>

          {token ? (
            <a href="/maps" className="nav-link nav-link-cta">Console</a>
          ) : (
            <a href="/login" className="nav-link nav-link-cta">{t('home.hero.getStarted', 'Get Started')}</a>
          )}
        </nav>
      </header>

      {/* 滚动提示（Hero 阶段才出现，极简） */}
      {showHint && sectionRuntime.id === 'hero' && (
        <div className="indoor-story-hint" aria-hidden="true">
          <div className="hint-icon">
            <svg viewBox="0 0 24 24" className="hint-arrow">
              <path d="M12 5v14M19 12l-7 7-7-7" />
            </svg>
          </div>
        </div>
      )}

      {/* 文本内容（单段渲染，严格同步场景） */}
      {currentText && (
        <div
          key={sectionRuntime.id}
          className={`indoor-story-content ${sectionRuntime.id === 'hero' ? 'content-hero' : 'content-side'}`}
          style={{
            opacity: sectionRuntime.alpha,
            transform:
              sectionRuntime.id === 'hero'
                ? `translate(-50%, -50%) scale(${1 - sectionRuntime.localProgress * 0.015})`
                : undefined,
          }}
          data-phase={sectionRuntime.phase}
          data-transition={isHeroTransition ? 'deconstruct' : undefined}
        >
          <h1 className="content-title">{currentText.title}</h1>
          <p className="content-subtitle">{currentText.subtitle}</p>
          {currentText.detail && <p className="content-detail">{currentText.detail}</p>}
          {currentText.button && (
            <a
              href={currentText.button.url}
              target="_blank"
              rel="noopener noreferrer"
              className="content-button"
            >
              {currentText.button.text}
            </a>
          )}
        </div>
      )}

      {/* 定价展示 - 简化版 */}
      {showPricing && (
        <div className="pricing-simple">
          <div className="pricing-header-text">
            <span className="pricing-early-access">{t('indoorStory.pricing.earlyAccess', 'Free during early access')}</span>
            <h2 className="pricing-title">{t('indoorStory.pricing.title', 'Free early access')}</h2>
            <p className="pricing-subtitle">{t('indoorStory.pricing.subtitle', 'Create and test interactive 3D indoor maps at no cost.')}</p>
            <p className="pricing-no-card">{t('indoorStory.pricing.noCard', 'No credit card required')}</p>
          </div>
          <div className="pricing-items">
            {pricingPlans.map((plan, index) => (
              <div key={index} className={`pricing-item ${plan.name === 'Professional' ? 'highlight' : ''}`}>
                <span className="pricing-item-name">{plan.name}</span>
                <span className="pricing-item-price">{plan.price}</span>
                <span className="pricing-item-features">{plan.features}</span>
              </div>
            ))}
          </div>
          <div className="pricing-feedback-copy">
            <strong>{t('indoorStory.pricing.feedbackInvite', 'Help shape KiMap')}</strong>
            <span>{t('indoorStory.pricing.feedbackDetail', 'Try the live project and tell us what your team needs.')}</span>
          </div>
          <div className="pricing-actions">
            <a
              href={withGrowthQuery('/editor/example')}
              target="_blank"
              rel="noopener noreferrer"
              className="pricing-btn pricing-btn-example"
              onClick={() => trackGrowthEvent('example_open', '/')}
            >
              {t('indoorStory.pricing.actions.getExample', 'Get Example')}
            </a>
            <a
              href={withGrowthQuery('/register')}
              className="pricing-btn pricing-btn-start"
              onClick={() => trackGrowthEvent('register_click', '/')}
            >
              {t('indoorStory.pricing.actions.startCreating', 'Start Creating')}
            </a>
          </div>
        </div>
      )}

      {/* 底部装饰 */}
      <div className="indoor-story-footer">
        <div className="footer-line" />
        <span className="footer-text">{t('indoorStory.footer.copyright', 'Kimap 2026')}</span>
        <a className="footer-email" href="mailto:kimap.founder@gmail.com">kimap.founder@gmail.com</a>
      </div>

      <FeedbackButton placement="floating" sourcePage="/" />

      {/* 右侧虚拟滚动条 */}
      <div className="virtual-scrollbar">
        <div className="scrollbar-track">
          <div
            className="scrollbar-thumb"
            style={{ top: `${(storyTimeline.findIndex(s => s.id === sectionRuntime.id) / (storyTimeline.length - 1)) * 100}%` }}
          />
        </div>
        <div className="scrollbar-sections">
          {storyTimeline.map((section, index) => {
            const isActive = sectionRuntime.id === section.id;
            const isPast = progress > section.end;
            return (
              <div
                key={section.id}
                className={`scrollbar-dot ${isActive ? 'active' : ''} ${isPast ? 'past' : ''}`}
                style={{ top: `${(index / (storyTimeline.length - 1)) * 100}%` }}
                title={t(`indoorStory.${section.id}.title`, section.id)}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default IndoorStory;
